import React, { useState, useEffect, useCallback } from 'react';
import { fetchFeedback, deleteFeedback, exportFeedbackCsv } from '../../api/client';
import formatDate from '../../utils/formatDate';
import './FeedbackList.css';

const QUESTION_LABELS = {
  planning: 'Planning',
  communication: 'Communication',
  booking_process: 'Booking Process',
  payment_process: 'Payment Process',
  transportation: 'Transportation',
  arrival_room_allocation: 'Arrival & Room Allocation',
  feeding_arrangements: 'Feeding Arrangements',
  church_growth_programs: 'Church Growth Programs',
  checkout_departure: 'Check-out & Departure',
  overall_assessment: 'Overall Assessment',
};

function getRatingBadge(rating) {
  if (rating >= 8) return 'badge-positive';
  if (rating >= 5) return 'badge-pending';
  return 'badge-negative';
}

export default function FeedbackList() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [skip, setSkip] = useState(0);
  const [exporting, setExporting] = useState(false);
  const limit = 20;

  const loadFeedbacks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFeedback({ limit, skip });
      setFeedbacks(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmId === id) {
      try {
        await deleteFeedback(id);
        setConfirmId(null);
        loadFeedbacks();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmId(id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmId(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportFeedbackCsv();
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading && feedbacks.length === 0) {
    return <div className="loading">Loading feedback...</div>;
  }

  if (error) {
    return <div className="error-message">Failed to load feedback: {error}</div>;
  }

  if (feedbacks.length === 0) {
    return <div className="card"><p>No feedback responses yet.</p></div>;
  }

  const hasMore = skip + limit < total;
  const hasPrev = skip > 0;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Feedback Responses</h3>
        <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Attendee</th>
            <th>Phone</th>
            <th>Avg Rating</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {feedbacks.map((fb) => (
            <React.Fragment key={fb._id}>
              <tr
                onClick={() => toggleExpand(fb._id)}
                style={{ cursor: 'pointer' }}
              >
                <td>{formatDate(fb.created_at)}</td>
                <td>{fb.attendee_name || 'N/A'}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {fb.attendee_phone || fb.phone_number || 'N/A'}
                </td>
                <td>
                  <span className={`badge ${getRatingBadge(fb.average_rating)}`}>
                    {fb.average_rating ? fb.average_rating.toFixed(1) : '—'} / 10
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  {confirmId === fb._id ? (
                    <>
                      <button className="btn-danger-sm" onClick={(e) => handleDelete(e, fb._id)}>Confirm</button>
                      {' '}
                      <button className="btn-secondary-sm" onClick={handleCancelDelete}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn-icon" onClick={(e) => handleDelete(e, fb._id)} title="Delete">&#128465;</button>
                  )}
                </td>
              </tr>
              {expandedId === fb._id && (
                <tr className="feedback-detail-row">
                  <td colSpan={5}>
                    <div className="feedback-details">
                      <div className="feedback-meta">
                        <span className="detail-label">Call ID:</span>{' '}
                        <span className="detail-value">{fb.call_id || 'N/A'}</span>
                      </div>
                      <div className="feedback-ratings-grid">
                        {Object.entries(QUESTION_LABELS).map(([key, label]) => {
                          const entry = (fb.ratings || {})[key];
                          if (!entry) return null;
                          return (
                            <div key={key} className="feedback-rating-item">
                              <div className="feedback-rating-header">
                                <span className="feedback-question-label">{label}</span>
                                <span className={`badge ${getRatingBadge(entry.rating)}`}>
                                  {entry.rating}/10
                                </span>
                              </div>
                              {entry.comment && (
                                <div className="feedback-comment">{entry.comment}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {(hasPrev || hasMore) && (
        <div className="submission-pagination">
          <button
            className="btn-secondary"
            disabled={!hasPrev}
            onClick={() => setSkip((s) => Math.max(0, s - limit))}
          >
            Previous
          </button>
          <span className="pagination-info">
            {skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasMore}
            onClick={() => setSkip((s) => s + limit)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
